import { useState, useEffect, useCallback } from 'react';
import type { FolderApi, Folder, Project, NewAPIResponse } from '../services/folder.service';
import { fetchFolderChildren, fetchCompleteTree } from '../services/folder.service';
import { ITestCase } from '../types/interfaces/testCase.interface';

// State for tracking loaded test cases
interface TestCaseLoadingState {
  loading: boolean;
  refreshing?: boolean;
  data: ITestCase[] | null;
  error?: string | null;
}

// Result of transforming the new API response
interface TransformResult {
  folders: Folder[];
  testCaseData: Record<number, TestCaseLoadingState>;
  allProjectsFromTree: Project[];
}

/**
 * Transform the new API response format to match the existing structure
 * Extracts test cases into separate state and renames "children" to "folders"
 */
function transformNewAPIToExisting(apiResponse: NewAPIResponse[]): TransformResult {
  const testCaseData: Record<number, TestCaseLoadingState> = {};
  const allProjectsFromTree: Project[] = [];
  
  function transformFolder(node: NewAPIResponse): Folder {
    // Collect all projects for tracking
    node.projects.forEach(project => {
      const transformedProject: Project = {
        testProjectID: project.testProjectID,
        name: project.name,
        remark: project.remark,
        lastModifiedTime: project.lastModifiedTime,
        channelType: project.channelType,
        hasSchedule: project.hasSchedule, // Default to false as discussed
        hasEnabledSchedule: false // Default to false as discussed
      };
      allProjectsFromTree.push(transformedProject);
      
      // Store test cases separately (mimics current behavior)
      testCaseData[project.testProjectID] = {
        loading: false,
        data: project.testCases || [],
        error: null
      };
    });
    
    // Transform projects WITHOUT testCases field to match existing structure
    const transformedProjects = node.projects.map(project => ({
      testProjectID: project.testProjectID,
      name: project.name,
      remark: project.remark,
      lastModifiedTime: project.lastModifiedTime,
      channelType: project.channelType,
      hasSchedule: project.hasSchedule, // Preserve if available
      hasEnabledSchedule: false
    }));
    
    return {
      folderId: node.folderId,
      name: node.name,
      parentId: node.parentId,
      projects: transformedProjects,
      folders: node.children.map(transformFolder) // Recursive transformation
    };
  }
  
  // Find the root folder (folderId: 0) and use its children as top-level folders
  // This avoids duplicate "folder-root" ID conflict with the hardcoded root in FolderTree.tsx
  const rootFolder = apiResponse.find(folder => folder.folderId === 0);
  
  if (rootFolder) {
    // Process the root folder's projects (if any) and its children
    // but don't include the root folder itself in the output
    rootFolder.projects.forEach(project => {
      const transformedProject: Project = {
        testProjectID: project.testProjectID,
        name: project.name,
        remark: project.remark,
        lastModifiedTime: project.lastModifiedTime,
        channelType: project.channelType,
        hasSchedule: project.hasSchedule,
        hasEnabledSchedule: false
      };
      allProjectsFromTree.push(transformedProject);
      
      testCaseData[project.testProjectID] = {
        loading: false,
        data: project.testCases || [],
        error: null
      };
    });
    
    // Return root's children as top-level folders
    return {
      folders: rootFolder.children.map(transformFolder),
      testCaseData,
      allProjectsFromTree
    };
  }
  
  // Fallback: if no root folder found, transform all folders
  // (this shouldn't happen with the expected API response)
  console.warn('No root folder (ID: 0) found in API response, using all folders');
  return {
    folders: apiResponse.map(transformFolder),
    testCaseData,
    allProjectsFromTree
  };
}

/**
 * Recursively build out the tree by fetching children for each FolderApi node.
 */
async function buildTree(nodes: FolderApi[] = []): Promise<Folder[]> {
  const tree = await Promise.all(
    nodes.map(async (node) => {
      const children = await fetchFolderChildren(node.folderId);
      const subfolders = children.length > 0 ? await buildTree(children) : [];
      return { ...node, folders: subfolders };
    })
  );
  return tree;
}

/**
 * useFolderTree fetches the entire nested folder tree.
 * Returns an object with `folders` (the tree), `loading` flag, `refetchFolders` function,
 * and preloaded test cases when using the new API.
 */
export function useFolderTree() {
  const [folders, setFolders] = useState<Folder[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [preloadedTestCases, setPreloadedTestCases] = useState<Record<number, TestCaseLoadingState>>({});
  const [isUsingNewAPI, setIsUsingNewAPI] = useState(false);

  const fetchTreeData = useCallback(async (setLoadingState = true) => {
    if (setLoadingState) setLoading(true);
    try {
      // Use new API by default, only disable if explicitly set to 'false'
      const useNewAPI = process.env.REACT_APP_USE_NEW_TREE_API !== 'false' && 
                        localStorage.getItem('USE_NEW_TREE_API') !== 'false';
      
      if (useNewAPI) {
        // NEW PATH: Single API call for everything
        console.log('Using new tree API to fetch complete folder structure');
        const apiResponse = await fetchCompleteTree();
        const { folders: transformedFolders, testCaseData } = transformNewAPIToExisting(apiResponse);
        setFolders(transformedFolders);
        setPreloadedTestCases(testCaseData);
        setIsUsingNewAPI(true);
        console.log(`Loaded ${transformedFolders.length} root folders with ${Object.keys(testCaseData).length} projects' test cases preloaded`);
      } else {
        // EXISTING PATH: Recursive tree building
        console.log('Using legacy recursive API to build folder tree');
        const roots = await fetchFolderChildren(0);
        const tree = await buildTree(roots);
        setFolders(tree);
        setPreloadedTestCases({});
        setIsUsingNewAPI(false);
      }
    } catch (error) {
      console.error('Error loading folder tree', error);
      setFolders([]);
      setPreloadedTestCases({});
      setIsUsingNewAPI(false);
    } finally {
      if (setLoadingState) setLoading(false);
    }
  }, []);

  useEffect(() => {
    let mounted = true;
    fetchTreeData().finally(() => {
      if (mounted) {
        // No need to set loading false again here, fetchTreeData handles it.
      }
    });
    return () => {
      mounted = false;
    };
  }, [fetchTreeData]);

  return { 
    folders, 
    loading, 
    refetchFolders: fetchTreeData,
    preloadedTestCases,
    isUsingNewAPI
  };
} 